import React from 'react';
import styles from './ui-badge.module.scss';

type BadgeVariant =
    | 'gray'
    | 'primary'
    | 'success'
    | 'error'
    | 'warning'
    | 'info'
    | 'dark'
    | 'deal';
type BadgeSize = 'sm' | 'md';

interface UIBadgeProps {
    variant?: BadgeVariant;
    size?: BadgeSize;
    pill?: boolean;
    children: React.ReactNode;
    className?: string;
}

export function UIBadge({
    variant = 'gray',
    size = 'md',
    pill = false,
    children,
    className = '',
}: UIBadgeProps) {
    const classes = [
        styles.badge,
        styles[`variant-${variant}`],
        styles[`size-${size}`],
        pill ? styles.pill : '',
        className,
    ].filter(Boolean).join(' ');

    return <span className={classes}>{children}</span>;
}

const conditionColors: Record<string, BadgeVariant> = {
    'Like New': 'success',
    'Excellent': 'success',
    'Good': 'info',
    'Fair': 'warning',
    'Poor': 'error',
};

const conditionDescriptions: Record<string, string> = {
    'Like New': 'Item appears unused or barely used. No visible wear, scratches, or marks. All original accessories included.',
    'Excellent': 'Minimal signs of use. May have very faint marks that are barely noticeable. Fully functional, well cared for.',
    'Good': 'Normal signs of use such as light scuffs or minor cosmetic wear. Fully functional with no impact on performance.',
    'Fair': 'Visible wear including scratches, scuffs, or cosmetic damage. Fully functional but shows clear signs of regular use.',
    'Poor': 'Heavy wear or cosmetic damage. May have dents, deep scratches, or missing accessories. Functional but well-worn.',
};

const conditionDotSlugs: Record<string, string> = {
    'Like New': 'like-new',
    'Excellent': 'excellent',
    'Good': 'good',
    'Fair': 'fair',
    'Poor': 'poor',
};

interface UIConditionBadgeProps {
    label: string;
    /** `pill` renders a filled badge; `dot` renders a colored dot + label. */
    variant?: 'pill' | 'dot';
    size?: BadgeSize;
    className?: string;
}

export function UIConditionBadge({
    label,
    variant = 'pill',
    size = 'md',
    className = '',
}: UIConditionBadgeProps) {
    const description = conditionDescriptions[label];

    if (variant === 'dot') {
        const slug = conditionDotSlugs[label];
        const dotClasses = [
            styles.conditionDot,
            slug ? styles[`dot-${slug}`] : '',
        ].filter(Boolean).join(' ');

        return (
            <span
                className={[
                    styles.conditionBadgeWrap,
                    styles.conditionDotWrap,
                    styles[`size-${size}`],
                    className,
                ].filter(Boolean).join(' ')}
                title={description}
            >
                <span className={dotClasses} aria-hidden="true" />
                {label}
            </span>
        );
    }

    const badgeVariant = conditionColors[label] || 'gray';
    return (
        <span className={styles.conditionBadgeWrap} title={description}>
            <UIBadge variant={badgeVariant} size={size} pill className={className}>
                {label}
            </UIBadge>
        </span>
    );
}

export default UIBadge;
